import {z} from 'zod';
import {ObjectIdSchema} from '../../zod';
import {ApiDefinition, BatchedApiDefinition} from '../definitions';
import {
  LegacyAiResearchFieldModeSchema,
  GptModelSchema,
  GptToolSchema,
} from '../../fields/enrichment';
import {GenericTaskSchema, TaskTypeSchema} from '../../task';
import {KeyplayUser, KeyplayUserSchema} from '../../auth';
import {PlanSchema} from '../../customer';
import {AccountTierAndSegmentSchema} from '../../insights';

export const GetResearchPrompt = {
  path: 'debug/researchPrompt',
  method: 'get',
  options: {access: 'admin'},
  requestDataSchema: z.object({
    plan: z.string(),
    mode: LegacyAiResearchFieldModeSchema,
    accountId: ObjectIdSchema,
  }),
  responseDataSchema: z
    .object({
      prompt: z.string(),
    })
    .nullable(),
} satisfies ApiDefinition;

export const GetAiDebugCompletion = {
  path: 'debug/aiCompletion',
  method: 'get',
  options: {access: 'admin'},
  requestDataSchema: z.object({
    model: GptModelSchema,
    prompt: z.string(),
    tools: GptToolSchema.array().min(1).optional(),
    temperature: z.number().min(0).max(2).optional(),
    // TODO: allow JSON schema that we convert to zod schema?
  }),
  responseDataSchema: z
    .object({
      result: z.string(),
      messages: z.unknown().array(),
      usage: z.unknown(),
    })
    .nullable(),
} satisfies ApiDefinition;

export const ClientTaskSchema = GenericTaskSchema.pick({
  debug: true,
  detailedState: true,
  error: true,
  initiatedBy: true,
  input: true,
  name: true,
  pendingCancel: true,
  state: true,
  timestamp: true,
  type: true,
  updates: true,
}).extend({
  // handle cases where client types are out of date with server types
  type: TaskTypeSchema.or(z.string()),
});
export type ClientTask = z.infer<typeof ClientTaskSchema>;
export const GetTasks = {
  path: 'debug/tasks',
  method: 'get',
  options: {access: 'admin'},
  requestDataSchema: z.object({
    partialCustomerName: z.string().optional(),
  }),
  responseDataSchema: ClientTaskSchema.array(),
} satisfies ApiDefinition;

export const CancelTask = {
  path: 'debug/cancelTask',
  method: 'post',
  options: {access: 'admin'},
  requestDataSchema: z.object({
    taskName: z.string(),
  }),
  responseDataSchema: z.void(),
} satisfies ApiDefinition;

export const GetKeyplayUsersMaxBatchSize = 50;
export const GetKeyplayUsers: BatchedApiDefinition<
  {userIds: string[]},
  KeyplayUser[],
  string,
  KeyplayUser
> = {
  path: 'debug/users',
  method: 'get',
  options: {access: 'admin'},
  requestDataSchema: z.object({
    userIds: z.string().array(),
  }),
  responseDataSchema: KeyplayUserSchema.array(),
  itemSchema: z.string(),
  itemResponseDataSchema: KeyplayUserSchema,

  maxBatchSize: GetKeyplayUsersMaxBatchSize,
  createParams: (userIds) => ({
    userIds,
  }),
  resolveItem: (users, userId) => users.find(({id}) => id === userId) ?? null,
};

export const GetCustomers = {
  path: 'debug/customers',
  method: 'get',
  options: {access: 'admin'},
  requestDataSchema: z.object({}),
  responseDataSchema: z
    .object({
      _id: ObjectIdSchema,
      name: z.string(),
      plan: PlanSchema,
    })
    .array(),
} satisfies ApiDefinition;

export const DeleteCustomerAccounts = {
  path: 'debug/deleteCustomerAccounts',
  method: 'post',
  options: {access: 'admin'},
  requestDataSchema: z.object({customerId: ObjectIdSchema}),
  responseDataSchema: z.void(),
} satisfies ApiDefinition;

export const MarketReportMinCustomerSampleSize = 30;
export const GenerateMarketReport = {
  path: 'debug/generateMarketReport',
  method: 'post',
  options: {access: 'admin'},
  requestDataSchema: z.object({
    domain: z.string(),
    testListId: ObjectIdSchema,
    marketId: ObjectIdSchema,
  }),
  responseDataSchema: z.object({
    taskId: ObjectIdSchema,
  }),
} satisfies ApiDefinition;

export const GetMarketReports = {
  path: 'debug/marketReports',
  method: 'get',
  options: {access: 'admin'},
  requestDataSchema: z.object({marketId: ObjectIdSchema}),
  responseDataSchema: z
    .object({
      _id: ObjectIdSchema,
      marketId: ObjectIdSchema,
      domain: z.string(),
      testListId: ObjectIdSchema,
      timestamp: z.date(),
    })
    .array(),
} satisfies ApiDefinition;

export const GetMarketReport = {
  path: 'debug/marketReport',
  method: 'get',
  options: {access: 'admin'},
  requestDataSchema: z.object({reportId: ObjectIdSchema}),
  responseDataSchema: z.object({
    prompts: z.string(),
    report: z.string(),
    customerAccountTiers: AccountTierAndSegmentSchema.array(),
  }),
} satisfies ApiDefinition;

export const GetWebIntentCustomers = {
  path: 'debug/webIntentCustomers',
  method: 'get',
  options: {access: 'admin'},
  requestDataSchema: z.object({}),
  responseDataSchema: z
    .object({
      _id: ObjectIdSchema,
      name: z.string(),
      firstDataReceivedAt: z.date().optional(),
      trackingScriptDetectedAt: z.date().optional(),
      webIntentIdentificationsPerMonth: z.object({
        limit: z.number(),
        current: z.number(),
      }),
    })
    .array(),
} satisfies ApiDefinition;
